import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom'
import Button from '../components/ui/button'
import { ChevronRight } from 'lucide-react'
import CardColor from '../assets/images/icons/card-icon.png'
import onlineTagIcon from '../assets/images/icons/onlinetag.png'
import Modal from '../components/ui/modal'
import { useTenant } from '../context/TenantContext'
import { useOffers } from '../context/OffersContext'
import { useLanguage } from '../context/LanguageContext'
import { useClient } from '../context/ClientContext'
import { useCards } from '../context/CardContext'

import apiClient from '../services/api.service'
import { getResponsiveImages } from '../utils/imageUtils'
import { formatValidUntil } from '../utils/dateFormatter'
import {
  ShimmerCircularImage,
  ShimmerText,
  ShimmerThumbnail,
  ShimmerTitle,
} from 'react-shimmer-effects'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

const AutoScrollCarousel = ({ items = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (items.length === 0) return
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length)
    }, 3000) // Auto-scroll every 3 seconds

    return () => clearInterval(interval)
  }, [items.length])

  if (items.length === 0) {
    return null
  }

  return (
    <div className="w-full overflow-hidden">
      <div className="relative">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {items.map((item, index) => (
            <div key={item.id || index} className="flex-shrink-0 w-full">
              {/* Mobile banner */}
              <img
                src={item.imageMobile || item.image}
                alt={`Carousel ${index + 1}`}
                className="block md:hidden w-full border border-gray-300 rounded-md"
                loading="lazy"
                onError={(e) => {
                  // Fallback to default image if CDN image fails to load
                  e.target.src = require('../assets/images/backgrounds/home-banner.png')
                }}
              />
              {/* Desktop banner */}
              <img
                src={item.imageWeb || item.image}
                alt={`Carousel ${index + 1}`}
                className="hidden md:block w-full border border-gray-300 rounded-md"
                loading="lazy"
                onError={(e) => {
                  // Fallback to default image if CDN image fails to load
                  e.target.src = require('../assets/images/backgrounds/home-banner.png')
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-3">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              currentIndex === index ? 'bg-theme-primary' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

const HomePageSkeleton = () => {
  const BannerSkeleton = () => (
    <div className="main-banner">
      <div className="w-full overflow-hidden mb-6">
        {/* Match AutoScrollCarousel image area */}
        <div className="md:hidden">
          <ShimmerThumbnail height={181} rounded />
        </div>
        <div className="hidden md:block">
          <ShimmerThumbnail height={420} rounded />
        </div>

        {/* Match carousel dots */}
        <div className="flex justify-center gap-2 mt-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="opacity-80">
              <ShimmerCircularImage size={10} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const FeaturedCard = ({ idx }) => (
    <div
      key={idx}
      className="bg-white rounded-[10px] shadow-md overflow-hidden w-[263px] h-[245px] flex-shrink-0"
    >
      <ShimmerThumbnail width={263} height={117} />
      <div className="px-2 py-2">
        <div className="flex items-center">
          <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center overflow-hidden">
            <ShimmerCircularImage size={38} />
          </div>
          <div className="ml-2 w-28">
            <ShimmerTitle line={1} gap={10} variant="secondary" />
          </div>
        </div>
        <div className="mt-3">
          <ShimmerText line={2} gap={10} />
        </div>
        <div className="mt-3 w-28">
          <ShimmerTitle line={1} gap={10} variant="secondary" />
        </div>
      </div>
    </div>
  )

  const FeaturedSliderSkeleton = () => (
    <div className="feature-slider px-3">
      <div className="mt-4 mb-2 w-24">
        <ShimmerTitle line={1} gap={10} variant="primary" />
      </div>
      <div className="relative">
        <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <FeaturedCard key={i} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const OfferGridCard = ({ idx }) => (
    <div
      key={idx}
      className="bg-white rounded-2xl shadow-md overflow-hidden border-[0.5px]"
    >
      <ShimmerThumbnail height={117} />
      <div className="px-2 py-2">
        <div className="flex items-center">
          <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center overflow-hidden">
            <ShimmerCircularImage size={38} />
          </div>
          <div className="ml-2 w-24">
            <ShimmerTitle line={1} gap={10} variant="secondary" />
          </div>
        </div>
        <div className="mt-3">
          <ShimmerText line={2} gap={10} />
        </div>
        <div className="mt-4 w-24">
          <ShimmerTitle line={1} gap={10} variant="secondary" />
        </div>
      </div>
    </div>
  )

  return (
    <div className="home-page bg-white">
      <BannerSkeleton />

      <FeaturedSliderSkeleton />

      <div className="w-full px-3">
        <div className="offers-section mt-6">
          <div className="flex items-center justify-between">
            <div className="mt-4 mb-2 w-20">
              <ShimmerTitle line={1} gap={10} variant="primary" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <OfferGridCard key={i} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const HomePage = () => {
  const { t, languageCode } = useLanguage()
  const { tenant, isLoading: tenantLoading } = useTenant()
  const { client, isAuthenticated } = useClient()
  const { cards, loading: cardsLoading } = useCards()
  const {
    offers,
    featuredOffers,
    loading: offersLoading,
    error: offersError,
    lastUpdated: offersLastUpdated,
    loadingMore,
    hasMore,
    loadMore,
    setCardNumber,
  } = useOffers()
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const [banners, setBanners] = useState([])
  const [featuredData, setFeaturedData] = useState([])
  const [offersData, setOffersData] = useState([])
  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') || ''
  const loadMoreRef = useRef(null)
  const hasShownWelcome = useRef(false)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [selectedCardNumber, setSelectedCardNumber] = useState('')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [sortOrder, setSortOrder] = useState('popularity')
  const [activatedOfferIds, setActivatedOfferIds] = useState(new Set())
  const [localActivationExpiry, setLocalActivationExpiry] = useState({})
  const [showActivationSuccessModal, setShowActivationSuccessModal] = useState(false)
  const showCardSelector = isAuthenticated && cards && cards.length > 0
  const cardLoading = cardsLoading && isAuthenticated

  const welcomeImageFilename = tenant?.tenantmasterconfig?.welcomeimage || ''
  const welcomeImageUrl = welcomeImageFilename.trim()
    ? `${process.env.REACT_APP_CLOUDFRONT}icons/${welcomeImageFilename}`
    : null

  // Feature flags from masterconfig
  const showCategories = tenant?.tenantmasterconfig?.category?.show === true
  const showSorting = tenant?.tenantmasterconfig?.sorting?.show === true
  const offerCardConfig = tenant?.tenantmasterconfig?.offer_card || {}
  const imageRatio = offerCardConfig.image_ratio
  const showSalesChannel = offerCardConfig.show_sales_channel === true
  const merchantLogoFill = offerCardConfig.merchant_logo_fill === true
  const categoryMappings = tenant?.tenantlanguagecontent?.categories || []
  const activationPeriod = tenant?.tenantmasterconfig?.offer_activation?.activation_period || 0

  // Handle first-time login welcome modal
  useEffect(() => {
    const showWelcomeEnabled =
      tenant?.tenantmasterconfig?.welcomemodal?.show !== false
    if (
      client?.isFirstTimeLogin &&
      !hasShownWelcome.current &&
      showWelcomeEnabled
    ) {
      hasShownWelcome.current = true

      // Use a small delay to ensure component is fully mounted
      setTimeout(() => {
        setShowWelcomeModal(true)
      }, 300)
    }
  }, [client, tenant])

  // Set default card for the UI card selector when cards are loaded.
  // NOTE: We no longer call setCardNumber here — OffersContext derives the
  // card to use internally from CardContext. setCardNumber is still called by
  // handleCardChange for manual card switching.
  useEffect(() => {
    if (cards && cards.length > 0 && !selectedCardNumber) {
      const defaultCard = cards.find((card) => card.isdefault === 1) || cards[0]
      setSelectedCardNumber(defaultCard.cardnumber)
    }
  }, [cards, selectedCardNumber])

  // Clear selected card when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedCardNumber('')
    }
  }, [isAuthenticated])

  // Handle card selection change
  const handleCardChange = (cardNumber) => {
    setSelectedCardNumber(cardNumber)
    setCardNumber(cardNumber)
  }

  // Handle offer click navigation
  const handleOfferClick = useCallback(
    (offerId) => {
      if (tenanturlname && offerId) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'cta_click', ctaName: 'offer_click', offerId: String(offerId) })
        navigate(`/${tenanturlname}/offer/${offerId}`)
      }
    },
    [navigate, tenanturlname],
  )

  // Handle signup button click
  const handleSignUpClick = useCallback(() => {
    if (tenanturlname) {
      navigate(`/${tenanturlname}/create-account`)
    }
  }, [navigate, tenanturlname])

  // Handle signin link click
  const handleSignInClick = useCallback(() => {
    if (tenanturlname) {
      navigate(`/${tenanturlname}/signin`)
    }
  }, [navigate, tenanturlname])

  // Category alias lookup from locale mapping
  const getCategoryAlias = useCallback(
    (catName) => {
      const mapping = categoryMappings.find((m) => m.category === catName)
      return mapping?.catalias || catName
    },
    [categoryMappings],
  )

  // Sales channel label mapping
  const getSalesChannelLabel = useCallback(
    (salesChannel) => {
      if (salesChannel === 'ALL') return t('homePage.salesChannelAll')
      if (salesChannel === 'E-COMMERCE') return t('homePage.salesChannelOnline')
      if (salesChannel) return t('homePage.salesChannelInStore')
      return null
    },
    [t],
  )

  const handleActivateOffer = useCallback(async (offerId) => {
    if (!isAuthenticated || !client) return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'cta_click', ctaName: 'offer_activate', offerId: String(offerId) })
    try {
      await apiClient.post('/api/offers/offerpostattribution', {
        externaluserid: client.externalUserId,
        offerid: offerId,
        action: 'ENROLL',
        tenantid: client.tenantId,
        clientid: client.clientId,
        cardnumber: selectedCardNumber || undefined,
      })
      setActivatedOfferIds((prev) => new Set([...prev, offerId]))
      if (activationPeriod > 0) {
        const expiry = new Date(Date.now() + activationPeriod * 60000).toISOString()
        setLocalActivationExpiry((prev) => ({ ...prev, [offerId]: expiry }))
      }
      setShowActivationSuccessModal(true)
    } catch (error) {
      console.error('Error activating offer:', error)
    }
  }, [isAuthenticated, client, selectedCardNumber, activationPeriod])
  // Toggle category selection (multi-select)
  const toggleCategory = useCallback((catName) => {
    if (catName === 'all') {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'cta_click', ctaName: 'category_filter', category: 'all' })
      setSelectedCategories([])
      return
    }
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'cta_click', ctaName: 'category_filter', category: catName })
    setSelectedCategories((prev) =>
      prev.includes(catName)
        ? prev.filter((c) => c !== catName)
        : [...prev, catName],
    )
  }, [])

  // Helper function to construct banner image URL
  const getBannerImageUrl = useCallback(
    (filename) => {
      if (!filename) return null
      // Remove any whitespace
      filename = filename.trim()
      // If it's already a full URL, return it
      if (filename.startsWith('http://') || filename.startsWith('https://')) {
        return filename
      }
      // Use CDN base URL from tenant context
      const cdnBase = tenant?.cdnBase
      if (!cdnBase) {
        return null
      }
      // Banner images might be in a specific path
      return `${cdnBase}/${filename}`
    },
    [tenant?.cdnBase],
  )

  // Extract banner images from tenant content config
  useEffect(() => {
    const heroImages = tenant?.tenantcontentconfig?.images?.home
    const cloudfront = process.env.REACT_APP_CLOUDFRONT
    const tenantUrlName = tenant?.tenanturlname

    // Check if we have hero images in content config
    if (
      heroImages &&
      Array.isArray(heroImages) &&
      heroImages.length > 0 &&
      cloudfront &&
      tenantUrlName
    ) {
      const bannerUrls = heroImages.map((filename, index) => ({
        id: index + 1,
        imageMobile: `${cloudfront}tenantbannerimages/${tenantUrlName}/mob_${filename}`,
        imageWeb: `${cloudfront}tenantbannerimages/${tenantUrlName}/web_${filename}`,
        image: `${cloudfront}tenantbannerimages/${tenantUrlName}/web_${filename}`, // Fallback
      }))
      setBanners(bannerUrls)
    } else {
      // Fallback to default banners if no content config images
      setBanners([
        {
          id: 1,
          image: require('../assets/images/backgrounds/home-banner.png'),
        },
        {
          id: 2,
          image: require('../assets/images/backgrounds/main-banner.png'),
        },
      ])
    }
  }, [tenant?.tenantcontentconfig?.images?.hero, tenant?.tenanturlname])

  // Transform offer to match component structure (for regular offers - uses card-img)
  const transformOffer = useCallback(
    (offer) => {
      const primaryMerchant = offer.targetMerchants?.[0]
      // Get responsive card-img images (both web and mobile)
      const cardImages = getResponsiveImages(offer.images, 'card-img')
      const fallbackImage =
        offer.images && offer.images.length > 0
          ? offer.images[0]
          : primaryMerchant?.image
            ? primaryMerchant.image
            : require('../assets/images/backgrounds/offer-img-1.png')

      // Use responsive images if available, otherwise fallback
      const imageUrl = cardImages.web || cardImages.mobile || fallbackImage
      const imageWeb = cardImages.web || imageUrl
      const imageMobile = cardImages.mobile || imageUrl

      const iconUrl =
        offer.offerLogo ||
        (primaryMerchant?.image
          ? primaryMerchant.image
          : require('../assets/images/icons/offer-icon-1.png'))

      const title = offer.title || 'Offer'
      const merchantName =
        offer.merchantDisplayName ||
        primaryMerchant?.name ||
        ''

      return {
        id: offer.id,
        image: imageUrl, // Fallback for browsers that don't support picture
        imageWeb: imageWeb,
        imageMobile: imageMobile,
        icon: iconUrl,
        title: title,
        merchantName: merchantName,
        description: offer.description || offer.title || '',
        validUntil: formatValidUntil(
          offer.validToUTC || offer.validTo,
          languageCode,
          t,
          { includeTime: false, timeZone: offer.timezone || undefined },
        ),
        featuredOffer: offer.featuredOffer || false,
        category: offer.targetMerchants?.[0]?.category?.name || '',
        salesChannel: offer.salesChannel || '',
        published: offer.published || null,
        validToUTC: offer.validToUTC || null,
        totalRedemptions: offer.totalRedemptions || 0,
      }
    },
    [languageCode, t],
  )

  // Transform featured offer (uses carousel-img)
  const transformFeaturedOffer = useCallback(
    (offer) => {
      const primaryMerchant = offer.targetMerchants?.[0]
      // Get responsive carousel-img images (both web and mobile)
      const carouselImages = getResponsiveImages(offer.images, 'carousel-img')
      const fallbackImage =
        offer.images && offer.images.length > 0
          ? offer.images[0]
          : primaryMerchant?.image
            ? primaryMerchant.image
            : require('../assets/images/backgrounds/offer-img-1.png')

      // Use responsive images if available, otherwise fallback
      const imageUrl =
        carouselImages.web || carouselImages.mobile || fallbackImage
      const imageWeb = carouselImages.web || imageUrl
      const imageMobile = carouselImages.mobile || imageUrl

      const iconUrl =
        offer.offerLogo ||
        (primaryMerchant?.image
          ? primaryMerchant.image
          : require('../assets/images/icons/offer-icon-1.png'))

      const title = offer.title || 'Offer'
      const merchantName =
        offer.merchantDisplayName ||
        primaryMerchant?.name ||
        ''

      return {
        id: offer.id,
        image: imageUrl, // Fallback for browsers that don't support picture
        imageWeb: imageWeb,
        imageMobile: imageMobile,
        icon: iconUrl,
        title: title,
        merchantName: merchantName,
        description: offer.description || offer.title || '',
        validUntil: formatValidUntil(
          offer.validToUTC || offer.validTo,
          languageCode,
          t,
          { includeTime: false, timeZone: offer.timezone || undefined },
        ),
        featuredOffer: offer.featuredOffer || false,
      }
    },
    [languageCode, t],
  )

  // Transform featured offers (from separate API call)
  useEffect(() => {
    if (!featuredOffers || featuredOffers.length === 0) {
      setFeaturedData([])
      return
    }

    const featured = featuredOffers.map(transformFeaturedOffer)
    setFeaturedData(featured)
  }, [featuredOffers, transformFeaturedOffer])

  // Transform regular offers data (paginated)
  useEffect(() => {
    if (!offers || offers.length === 0) {
      setOffersData([])
      return
    }

    // Transform all offers
    const allOffers = offers.map(transformOffer)
    setOffersData(allOffers)
  }, [offers, transformOffer])

  // Filter offers based on search query
  // const filteredOffers = useMemo(() => {
  //   if (!searchQuery.trim()) {
  //     return offersData;
  //   }

  //   const query = searchQuery.toLowerCase().trim();

  //   return offersData.filter((offer) => {
  //     // Search in: title, merchant name, description, and category
  //     const title = (offer.title || "").toLowerCase();
  //     const merchant = (offer.merchant || "").toLowerCase();
  //     const description = (offer.description || "").toLowerCase();
  //     const category = (offer.category || "").toLowerCase();

  //     return (
  //       title.includes(query) ||
  //       merchant.includes(query) ||
  //       description.includes(query) ||
  //       category.includes(query)
  //     );
  //   });
  // }, [offersData, searchQuery]);

  // Create effective search query that ignores initial load

  // Filter offers based on search query
  const filteredOffers = useMemo(() => {
    if (!searchQuery.trim()) {
      return offersData
    }

    const query = searchQuery.toLowerCase().trim()

    return offersData.filter((offer) => {
      // Search in: title, merchant name, description, and category
      const title = (offer.title || '').toLowerCase()
      const merchant = (offer.merchant || '').toLowerCase()
      const description = (offer.description || '').toLowerCase()
      const category = (offer.category || '').toLowerCase()

      return (
        title.includes(query) ||
        merchant.includes(query) ||
        description.includes(query) ||
        category.includes(query)
      )
    })
  }, [offersData, searchQuery])

  // Unique category names extracted from loaded offers (for category bar)
  const availableCategories = useMemo(() => {
    if (!offers || offers.length === 0) return []
    const cats = []
    for (const offer of offers) {
      const cat = offer.targetMerchants?.[0]?.category?.name
      if (cat && !cats.includes(cat)) cats.push(cat)
    }
    return cats
  }, [offers])

  // Filter by selected categories (multi-select)
  const categoryFilteredOffers = useMemo(() => {
    if (selectedCategories.length === 0) return filteredOffers
    return filteredOffers.filter((o) => selectedCategories.includes(o.category))
  }, [filteredOffers, selectedCategories])

  // Sort the category-filtered offers
  const displayedOffers = useMemo(() => {
    const list = [...categoryFilteredOffers]
    if (sortOrder === 'latest') {
      list.sort(
        (a, b) =>
          new Date(b.published || 0).getTime() -
          new Date(a.published || 0).getTime(),
      )
    } else if (sortOrder === 'endingsoon') {
      const FAR = new Date(8640000000000000).getTime()
      list.sort((a, b) => {
        const av = a.validToUTC ? new Date(a.validToUTC).getTime() : FAR
        const bv = b.validToUTC ? new Date(b.validToUTC).getTime() : FAR
        return av - bv
      })
    } else {
      // popularity (default)
      list.sort((a, b) => (b.totalRedemptions || 0) - (a.totalRedemptions || 0))
    }
    return list
  }, [categoryFilteredOffers, sortOrder])

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const currentRef = loadMoreRef.current
    if (!currentRef || !hasMore || loadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0]
        if (firstEntry.isIntersecting && hasMore && !loadingMore) {
          loadMore()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      },
    )

    observer.observe(currentRef)

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasMore, loadingMore, loadMore])

  // Avoid showing translation key-paths before language content is available.
  const translationsReady = !!tenant?.tenantlanguagecontent?.v3

  // Show loader until the first offers request completes.
  // Gate on tenant + tenanturlname + languageCode so we don't hang before OffersContext can fetch.
  const canFetchOffers = !!tenant?.tenantid && !!tenanturlname && !!languageCode
  const showInitialOffersLoader =
    canFetchOffers &&
    offersData.length === 0 &&
    !offersError &&
    (offersLoading || offersLastUpdated == null)

  if (tenantLoading || !translationsReady || showInitialOffersLoader) {
    // Shimmer skeleton (no key-path labels, no common Loader)
    return <HomePageSkeleton />
  }

  return (
    <>
      <div className="home-page">
        {/* BANNER - START */}
        <div className="main-banner">
          <div className="w-full overflow-hidden mb-6">
            <AutoScrollCarousel items={banners} />
          </div>
        </div>
        {/* BANNER - END */}

        {/* CARD SELECTOR - START */}
        {isAuthenticated && cards && cards.length > 0 && (
          <div className="px-3 mt-4 mb-2">
            <div className="flex md:items-center gap-3 justify-start flex-col md:flex-row items-start md:justify-end md:gap-3">
              <span className="text-sm font-semibold text-theme-secondary">
                {t('homePage.myCards')}
              </span>

              <Select
                value={selectedCardNumber}
                onValueChange={handleCardChange}
              >
                <SelectTrigger className="w-full md:w-72">
                  <SelectValue placeholder={t('homePage.myCards')} />
                </SelectTrigger>
                <SelectContent>
                  {cards && cards.length > 0 ? (
                    cards.map((card) => (
                      <SelectItem key={card.id} value={card.cardnumber}>
                        {card.cardalias
                          ? card.cardalias
                          : `•••• ${card.last4digits}`}
                        {card.isdefault === 1
                          ? ` (${t('common.default')})`
                          : ''}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-cards" disabled>
                      No cards saved
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {/* CARD SELECTOR - END */}

        {/* FEATURE SLIDER - START */}
        {tenant?.tenantmasterconfig?.enableFeaturedOffers !== false &&
          featuredData.length > 0 && (
            <div className="feature-slider px-3">
              <h2 className="text-sm font-extrabold text-theme-secondary text-left mt-4 mb-2">
                {t('homePage.featured')}
              </h2>
              <div className="relative group">
                <button
                  onClick={() => {
                    const container = document.getElementById(
                      'feature-slider-container',
                    )
                    const cardWidth = 263 + 12
                    container.scrollBy({ left: -cardWidth, behavior: 'smooth' })
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/10 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -ml-3 hover:scale-110"
                >
                  <ChevronRight
                    size={20}
                    className="rotate-180 text-theme-primary"
                  />
                </button>

                <button
                  onClick={() => {
                    const container = document.getElementById(
                      'feature-slider-container',
                    )
                    const cardWidth = 263 + 12
                    container.scrollBy({ left: cardWidth, behavior: 'smooth' })
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/10 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -mr-3 hover:scale-110"
                >
                  <ChevronRight size={20} className="text-theme-primary" />
                </button>

                <div
                  id="feature-slider-container"
                  className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                  <div
                    className="flex gap-3 pb-2"
                    style={{ width: 'max-content' }}
                  >
                    {featuredData.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleOfferClick(item.id)}
                        className="bg-white rounded-[10px] shadow-md overflow-hidden w-[263px] h-[245px] flex-shrink-0 transition-transform duration-300 ease-out hover:scale-[1.02] cursor-pointer"
                      >
                        <div className="overflow-hidden">
                          <picture>
                            {item.imageWeb &&
                              item.imageMobile &&
                              item.imageWeb !== item.imageMobile && (
                                <>
                                  <source
                                    media="(min-width: 768px)"
                                    srcSet={item.imageWeb}
                                  />
                                  <source
                                    media="(max-width: 767px)"
                                    srcSet={item.imageMobile}
                                  />
                                </>
                              )}
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-[263px] h-[117px] object-cover"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = require('../assets/images/backgrounds/featured-banner-1.png')
                              }}
                            />
                          </picture>
                        </div>
                        <div className="px-2 py-2">
                          <div className="flex items-center">
                            <div className="w-[38px] h-[38px] border border-theme-secondary-foreground rounded-full flex items-center justify-center">
                              <img
                                src={item.icon}
                                alt=""
                                className="w-6"
                                onError={(e) => {
                                  e.target.src = require('../assets/images/icons/featured-logo-1.png')
                                }}
                              />
                            </div>
                            <div className="text-[10px] font-semibold text-theme-sub-title ml-2">
                              {item.merchantName}
                            </div>
                          </div>
                          <p className="mt-3 font-extrabold text-xs text-theme-secondary text-left line-clamp-2">
                            {item.title}
                          </p>
                          {item.validUntil && (
                            <p className="my-3 text-[10px] font-semibold text-theme-sub-title text-left">
                              {item.validUntil}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        {/* FEATURE SLIDER - END */}

        {/* WELCOME TITLE - START */}
        {t('homePage.welcomeTitle') && (
          <div className="px-3 mt-6 md:mt-12">
            <h4 className="text-[20px] md:text-[22px] font-medium text-theme-secondary text-left">
              {t('homePage.welcomeTitle')}
            </h4>
          </div>
        )}
        {/* WELCOME TITLE - END */}

        {/* CATEGORY BAR - START */}
        {showCategories && availableCategories.length > 0 && (
          <div className="category-bar px-3 mt-[30px]">
            <div className="overflow-x-auto md:overflow-x-hidden md:hover:overflow-x-auto">
              <div className="flex gap-5 md:gap-[60px] pb-2" style={{ width: 'max-content' }}>
                {/* All button */}
                <div
                  onClick={() => toggleCategory('all')}
                  className="flex flex-col items-center cursor-pointer"
                >
                  <div
                    className={`w-[50px] h-[50px] rounded-full overflow-hidden border-[3px] flex items-center justify-center ${
                      selectedCategories.length === 0
                        ? 'border-theme-primary'
                        : 'border-transparent'
                    }`}
                  >
                    <img
                      src={`${process.env.REACT_APP_CLOUDFRONT}categories/${tenanturlname}/All.svg`}
                      alt={t('homePage.allCategories')}
                      className="w-full h-full object-none p-[5px]"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  </div>
                  <span className="text-xs mt-1 text-center font-medium text-theme-secondary whitespace-nowrap">
                    {t('homePage.allCategories')}
                  </span>
                </div>

                {/* Dynamic categories */}
                {availableCategories.map((catName) => (
                  <div
                    key={catName}
                    onClick={() => toggleCategory(catName)}
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <div
                      className={`w-[50px] h-[50px] rounded-full overflow-hidden border-[3px] flex items-center justify-center ${
                        selectedCategories.includes(catName)
                          ? 'border-theme-primary'
                          : 'border-transparent'
                      }`}
                    >
                      <img
                        src={`${process.env.REACT_APP_CLOUDFRONT}categories/${tenanturlname}/${encodeURIComponent(catName.replace(/\//g, ''))}.svg`}
                        alt={catName}
                        className="w-full h-full object-none p-[5px]"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                    <span className="text-xs mt-1 text-center font-medium text-theme-secondary whitespace-nowrap">
                      {getCategoryAlias(catName)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected category badges */}
            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedCategories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-theme-primary text-white"
                  >
                    {getCategoryAlias(cat)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleCategory(cat)
                      }}
                      className="text-white hover:text-gray-200 ml-1 font-bold leading-none"
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {/* CATEGORY BAR - END */}

        {/* OFFERS - START */}
        <div className="w-full px-3">
          <div className="offers-section mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-extrabold text-theme-secondary mt-4 mb-2">
                {searchQuery
                  ? t('homePage.results') + ` "${searchQuery}"`
                  : t('homePage.offers')}
              </h2>
              {showSorting && (
                <div className="flex items-center gap-2 mt-4 mb-2">
                  <span className="text-[14px] text-theme-secondary hidden md:inline">
                    {t('homePage.sortBy')}:
                  </span>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger className="h-7 text-[11px] rounded-full w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="popularity" hideIndicator>
                        {t('homePage.sortPopular')}
                      </SelectItem>
                      <SelectItem value="latest" hideIndicator>
                        {t('homePage.sortLatest')}
                      </SelectItem>
                      <SelectItem value="endingsoon" hideIndicator>
                        {t('homePage.sortEndingSoon')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {displayedOffers.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-[30px]">
                {displayedOffers.map((offer) => (
                  <div
                    key={offer.id}
                    onClick={() => handleOfferClick(offer.id)}
                    className="bg-white rounded-2xl shadow-md overflow-hidden transition-transform duration-300 ease-out hover:scale-[1.02] border-[0.5px] hover:border-theme-primary transition cursor-pointer relative"
                  >
                    <div className="overflow-hidden">
                      <picture>
                        {offer.imageWeb &&
                          offer.imageMobile &&
                          offer.imageWeb !== offer.imageMobile && (
                            <>
                              <source
                                media="(min-width: 768px)"
                                srcSet={offer.imageWeb}
                              />
                              <source
                                media="(max-width: 767px)"
                                srcSet={offer.imageMobile}
                              />
                            </>
                          )}
                        <img
                          src={offer.image}
                          alt={offer.title}
                          className={`w-full ${imageRatio === '1:1' ? 'aspect-square' : 'h-[117px] max-[450px]:h-auto'} object-cover`}
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = require('../assets/images/backgrounds/offer-img-1.png')
                          }}
                        />
                      </picture>
                    </div>
                    <div className={`px-2 pt-2 ${showSalesChannel ? 'pb-7' : 'pb-2'}`}>
                      <div className="flex items-center">
                        <div className={`w-[38px] h-[38px] border border-theme-secondary-foreground rounded-full flex items-center justify-center ${merchantLogoFill ? 'overflow-hidden' : ''}`}>
                          <img
                            src={offer.icon}
                            alt=""
                            className={merchantLogoFill ? 'w-full h-full object-cover' : 'w-6'}
                            onError={(e) => {
                              e.target.src = require('../assets/images/icons/offer-icon-1.png')
                            }}
                          />
                        </div>
                        <div className="text-[10px] font-semibold text-theme-sub-title ml-2">
                          {offer.merchantName}
                        </div>
                      </div>
                      <p className="mt-3 font-extrabold text-xs text-theme-secondary text-left line-clamp-2">
                        {offer.title}
                      </p>
                      {offer.validUntil && (
                        <p className="mt-6 text-[10px] font-semibold text-theme-sub-title text-left">
                          {offer.validUntil}
                        </p>
                      )}
                    </div>
                    {showSalesChannel && offer.salesChannel && (
                      <div className="absolute bottom-[5px] left-[15px] flex items-center gap-1">
                        <img
                          src={onlineTagIcon}
                          alt=""
                          className="w-[14px] h-[14px]"
                        />
                        <span className="text-[12px] font-semibold text-gray-700 uppercase tracking-[0.5px]">
                          {getSalesChannelLabel(offer.salesChannel)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-theme-sub-title whitespace-pre-line">
                {searchQuery
                  ? t('homePage.noResultsFound') + ` "${searchQuery}"`
                  : t('homePage.noOffersAvailable')}
              </div>
            )}

            {/* Infinite scroll trigger and loading indicator */}
            {displayedOffers.length > 0 && (
              <div ref={loadMoreRef} className="w-full py-4">
                {loadingMore && (
                  <div className="text-center py-4">
                    <div className="text-theme-secondary text-sm">
                      {t('homePage.loadingMoreOffers')}
                    </div>
                  </div>
                )}
                {!hasMore && !loadingMore && (
                  <div className="text-center py-4">
                    <div className="text-theme-sub-title text-sm">
                      {t('homePage.noMoreOffersToLoad')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Signup Section - Only show if user is not authenticated */}
          {!isAuthenticated && (
            <div className="text-center mt-8">
              <h2 className="font-bold text-sm text-theme-secondary">
                {t('homePage.signUpTitle')}
              </h2>
              <p className="mt-1 font-normal text-xs text-theme-secondary">
                {t('homePage.signUpSubtitle')}{' '}
                {tenant?.tenantmasterconfig?.howtoregisterpage?.show !==
                  false && (
                  <Link
                    to={`/${tenanturlname}/about`}
                    className="text-theme-primary no-underline hover:underline hover:text-theme-secondary"
                  >
                    {t('homePage.easySteps')}
                  </Link>
                )}
              </p>
              <div className="mt-6">
                <Button
                  id="home-btn"
                  variant="primary"
                  type="button"
                  size="lg"
                  onClick={handleSignUpClick}
                  className="max-w-[400px] mx-auto"
                >
                  <div className="flex items-center gap-2">
                    <div>
                      <img
                        src={CardColor}
                        alt="card icon"
                        className="w-4 h-4 object-contain"
                      />
                    </div>
                    <span>{t('homePage.signUpButton')}</span>
                  </div>
                </Button>
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  className="font-semibold text-xs text-theme-primary no-underline hover:underline hover:text-theme-secondary"
                  onClick={handleSignInClick}
                >
                  {t('homePage.alreadyHaveAccount')}
                </button>
              </div>

              <div className="mt-2 hidden">
                <p className="mt-1 font-normal text-xs text-theme-secondary">
                  {/* <Link
                    to={`/${tenanturlname}/ForgotPasswordGuide`}
                    className="text-theme-primary underline text-black no-underline cursor-default"
                  >
                  {t("homePage.passwordGuide")}
                </Link> */}
                  <div className="text-theme-primary">
                    {t('homePage.passwordGuide')}
                  </div>
                </p>
              </div>
            </div>
          )}
        </div>
        {/* OFFERS - END */}
      </div>

      {/* First Time Login Welcome Modal */}
      <Modal
        open={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onLinkCardClick={() =>
          navigate(`/${tenanturlname}/myprofile?tab=my-cards`)
        }
        tenanturlname={tenanturlname}
        showHowToRegister={
          tenant?.tenantmasterconfig?.howtoregisterpage?.show !== false
        }
        headerImage={welcomeImageUrl || undefined}
      />
    </>
  )
}

export default HomePage
